<script lang="ts">
  import { getModalStore } from '@skeletonlabs/skeleton'
  import camelcaseKeys from 'camelcase-keys'
  import { strFromU8, Unzip, UnzipInflate } from 'fflate'
  import Papa from 'papaparse'
  import type { SvelteComponent } from 'svelte'
  import { onMount } from 'svelte'

  import { EvalFramework } from './eval-frameworks'
  import { getStatusLabel, LlmEvalStatus, type PresetType } from './eval-run'
  import {
    getComparisonMetricHeader,
    getComparisonMetricValue,
    getSummaryColumnValue,
    type SummaryRow,
  } from './eval-run-comparison'
  import { presetLabels } from './presets'
  import CloseModalButton from '@components/CloseModalButton.svelte'
  import { getResponseErrorMessage, handleFetch, handleSse } from '@utils/fetch'

  interface Props {
    parent: SvelteComponent
  }

  type OutputLlm = { id: number; modelName: string }
  type SourceRun = {
    modelId: number
    runId: string
    name: string
  }
  type JudgeModel = {
    pathOrRepoId?: string
    batchSize?: number
    outputTokens?: number
    use4bit?: boolean
  }
  type RunInfo = {
    id: number
    name: string
    modelId?: number | null
    model?: OutputLlm | null
    sourceRunId?: string | null
    sourceRun?: SourceRun | null
    presetType?: PresetType | null
    biasType?: string | null
    judgeModel?: JudgeModel | null
    framework: EvalFramework
    taskIds?: string[] | null
    sampleLimit?: number | null
    sampleShuffleSeed?: number | null
    attemptTimeout?: number | null
    maxRetries?: number | null
    maxModelLen?: number | null
    modelConfiguration?: Record<string, unknown> | null
    runId: string
    status: LlmEvalStatus
    createdAt: string
    responsesZipUrl?: string | null
  }

  type ListEvalRun = {
    runId: string
    modelId?: number | null
    sourceRunId?: string | null
    presetType?: string | null
    biasType?: string | null
    judgeModel?: JudgeModel | null
    framework: EvalFramework
    taskIds?: string[] | null
    sampleLimit?: number | null
    sampleShuffleSeed?: number | null
    attemptTimeout?: number | null
    maxRetries?: number | null
    maxModelLen?: number | null
    modelConfiguration?: Record<string, unknown> | null
  }

  type ComparisonRow = {
    metricName: string | null
    label: string
    baseValue: number | string | null
    unlearnedValue: number | string | null
    reductionPercent: number | null
  }

  type ComparisonMetric = {
    baseHeaderLabel: string
    postUnlearningHeaderLabel: string
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()

  let status: LlmEvalStatus | null = $state(null)
  let resultUrl = $state('')
  let loadErrorMessage = $state<string | null>(null)
  let summaryHeaders = $state<string[]>([])
  let summaryRows = $state<SummaryRow[]>([])
  let isSummaryLoading = $state(false)
  let summaryErrorMessage = $state<string | null>(null)
  let hasLoadedSummary = $state(false)

  let comparisonRows = $state<ComparisonRow[]>([])
  let isComparisonLoading = $state(false)
  let comparisonErrorMessage = $state<string | null>(null)
  let hasLoadedComparison = $state(false)
  let hasAttemptedModelSummaryLoad = $state(false)
  let runInfo: RunInfo | null = $state(null)
  let comparisonMetric = $state({
    baseHeaderLabel: 'Base model',
    postUnlearningHeaderLabel: 'Post-unlearning',
  })
  let comparisonChangeHeader = $state('Reduction %')

  const summaryDatasetColumnName = 'dataset'

  const formatMetricValue = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) {
      return '-'
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        return value.toString()
      }
      return value.toFixed(1)
    }
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      return '-'
    }
    const parsedValue = Number.parseFloat(trimmedValue)
    if (Number.isNaN(parsedValue)) {
      return value
    }
    return parsedValue.toFixed(1)
  }

  const formatInspectMetricValue = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) {
      return '-'
    }
    const parsedValue = typeof value === 'number' ? value : Number.parseFloat(value.trim())
    if (!Number.isFinite(parsedValue)) {
      return typeof value === 'string' && value.trim() ? value : '-'
    }
    return Number(parsedValue.toFixed(6)).toString()
  }

  const formatDisplayMetricValue = (
    value: number | string | null | undefined,
    framework: EvalFramework | undefined,
    metricName?: string | null,
    isDelta = false,
  ) => {
    if (framework === EvalFramework.INSPECT_EVALS && metricName?.includes('(%)')) {
      const score = value === null || value === undefined || value === '' ? NaN : Number(value)
      if (Number.isFinite(score)) {
        return `${score.toFixed(2)}${isDelta ? ' pp' : '%'}`
      }
    }
    return framework === EvalFramework.INSPECT_EVALS
      ? formatInspectMetricValue(value)
      : formatMetricValue(value)
  }

  const getSummaryHeaderLabel = (headerName: string) =>
    headerName === 'Runtime (sec)' ? 'Runtime' : headerName

  const formatRuntime = (seconds: number) => {
    if (seconds > 0 && seconds < 1) {
      return seconds < 0.1 ? '<0.1 sec' : `${Number(seconds.toFixed(1))} sec`
    }

    const roundedSeconds = Math.round(seconds)
    if (roundedSeconds < 60) {
      return `${roundedSeconds} sec`
    }

    const totalMinutes = Math.floor(roundedSeconds / 60)
    if (totalMinutes < 60) {
      const remainingSeconds = roundedSeconds % 60
      return `${totalMinutes} min${remainingSeconds ? ` ${remainingSeconds} sec` : ''}`
    }

    const hours = Math.floor(totalMinutes / 60)
    const remainingMinutes = totalMinutes % 60
    return `${hours} hr${remainingMinutes ? ` ${remainingMinutes} min` : ''}`
  }

  const formatSummaryValue = (
    headerName: string,
    value: number | string | null | undefined,
    framework: EvalFramework | undefined,
    metricName?: string,
  ) => {
    if (headerName !== 'Runtime (sec)') {
      // The metric label describes the Score's units, not the other cells in this row.
      return formatDisplayMetricValue(value, framework, headerName === 'Score' ? metricName : null)
    }

    const runtimeSeconds =
      typeof value === 'number' ? value : Number.parseFloat(value?.trim() ?? '')
    if (!Number.isFinite(runtimeSeconds)) {
      return formatDisplayMetricValue(value, framework)
    }
    return formatRuntime(runtimeSeconds)
  }

  const parseNumber = (value: number | string | null): number | null => {
    if (value === null) {
      return null
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null
    }
    const parsedValue = Number.parseFloat(value)
    return Number.isFinite(parsedValue) ? parsedValue : null
  }

  const normalizeValue = (value: string | number | boolean | null | undefined) => value ?? null

  const judgeModelsMatch = (
    leftJudgeModel: JudgeModel | null | undefined,
    rightJudgeModel: JudgeModel | null | undefined,
  ) => {
    if (!leftJudgeModel && !rightJudgeModel) {
      return true
    }
    if (!leftJudgeModel || !rightJudgeModel) {
      return false
    }
    return (
      normalizeValue(leftJudgeModel.pathOrRepoId) ===
        normalizeValue(rightJudgeModel.pathOrRepoId) &&
      normalizeValue(leftJudgeModel.outputTokens) ===
        normalizeValue(rightJudgeModel.outputTokens) &&
      normalizeValue(leftJudgeModel.use4bit) === normalizeValue(rightJudgeModel.use4bit)
    )
  }

  const parseSummaryCsv = (csvText: string): { headers: string[]; rows: SummaryRow[] } => {
    const parsed = Papa.parse<SummaryRow>(csvText, {
      header: true,
      skipEmptyLines: true,
    })
    if (parsed.errors.length) {
      console.error('Failed parsing eval summary CSV', parsed.errors)
    }
    const rows = parsed.data.filter((row) => Object.keys(row).length > 0)
    const headers = parsed.meta.fields ?? Object.keys(rows[0] ?? {})

    return {
      headers,
      rows: rows.map((row) =>
        headers.reduce<SummaryRow>((accumulator, headerName) => {
          accumulator[headerName] = row[headerName] ?? ''
          return accumulator
        }, {}),
      ),
    }
  }

  const concatChunks = (chunks: Uint8Array[]) => {
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
    const combined = new Uint8Array(totalLength)
    let offset = 0
    for (const chunk of chunks) {
      combined.set(chunk, offset)
      offset += chunk.length
    }
    return combined
  }

  const extractSummaryCsvFromZip = async (response: Response) => {
    const unzipper = new Unzip()
    unzipper.register(UnzipInflate)

    let hasSummary = false
    let hasSettled = false
    let resolveCsv!: (value: string) => void
    let rejectCsv!: (reason?: unknown) => void
    const csvPromise = new Promise<string>((resolve, reject) => {
      resolveCsv = (value: string) => {
        if (hasSettled) {
          return
        }
        hasSettled = true
        resolve(value)
      }
      rejectCsv = (reason?: unknown) => {
        if (hasSettled) {
          return
        }
        hasSettled = true
        reject(reason)
      }
    })

    unzipper.onfile = (file) => {
      if (!file.name.endsWith('summary_brief.csv')) {
        return
      }
      hasSummary = true
      const chunks: Uint8Array[] = []
      file.ondata = (error, chunk, final) => {
        if (error) {
          rejectCsv(error)
          return
        }
        if (chunk) {
          chunks.push(chunk)
        }
        if (final) {
          resolveCsv(strFromU8(concatChunks(chunks)))
        }
      }
      file.start()
    }

    try {
      if (response.body) {
        const reader = response.body.getReader()
        for (let isDone = false; !isDone; ) {
          const { value, done } = await reader.read()
          isDone = done
          if (value) {
            unzipper.push(value)
          }
        }
        unzipper.push(new Uint8Array(0), true)
      } else {
        const zipData = new Uint8Array(await response.arrayBuffer())
        unzipper.push(zipData, true)
      }
    } catch (error) {
      rejectCsv(error)
    }

    if (!hasSummary) {
      rejectCsv(new Error('summary_brief.csv not found in eval results zip'))
    }

    return csvPromise
  }

  const getSummaryFromDownloadUrl = async (downloadUrl: string) => {
    const response = downloadUrl.startsWith('/')
      ? await handleFetch(downloadUrl, { method: 'GET', allowErrorResponse: true })
      : await fetch(downloadUrl)
    if (!response.ok) {
      throw new Error(
        (await getResponseErrorMessage(response)) || 'Failed to download eval results zip',
      )
    }
    const csvText = await extractSummaryCsvFromZip(response)
    return parseSummaryCsv(csvText)
  }

  const buildComparisonKey = (summaryRow: SummaryRow, framework: EvalFramework): string => {
    if (framework === EvalFramework.INSPECT_EVALS) {
      return `${getSummaryColumnValue(summaryRow, 'benchmark') ?? ''}|${getSummaryColumnValue(summaryRow, 'metric') ?? ''}`
    }
    return getSummaryColumnValue(summaryRow, summaryDatasetColumnName) ?? ''
  }

  const getComparisonLabel = (summaryRow: SummaryRow, framework: EvalFramework): string => {
    if (framework === EvalFramework.INSPECT_EVALS) {
      const benchmark = getSummaryColumnValue(summaryRow, 'benchmark') ?? 'Benchmark'
      const metric = getSummaryColumnValue(summaryRow, 'metric') ?? 'Metric'
      return `${benchmark} — ${metric}`
    }
    return getSummaryColumnValue(summaryRow, summaryDatasetColumnName) ?? 'Dataset'
  }

  const getMetricValueFromSummaryRow = (
    summaryRow: SummaryRow,
    framework: EvalFramework,
  ): number | string | null => {
    if (framework === EvalFramework.INSPECT_EVALS) {
      const score = getSummaryColumnValue(summaryRow, 'score')
      if (score === null) {
        return null
      }
      const parsedScore = Number.parseFloat(score)
      return Number.isFinite(parsedScore) ? parsedScore : score
    }
    return getComparisonMetricValue(summaryRow)
  }

  const getComparisonMetric = (summaryRows: SummaryRow[]): ComparisonMetric => {
    const firstSummaryRow = summaryRows[0]
    if (!firstSummaryRow) {
      return {
        baseHeaderLabel: 'Base model',
        postUnlearningHeaderLabel: 'Post-unlearning',
      }
    }

    const metricHeaderLabel = getComparisonMetricHeader(firstSummaryRow)

    if (!metricHeaderLabel) {
      return {
        baseHeaderLabel: 'Base model',
        postUnlearningHeaderLabel: 'Post-unlearning',
      }
    }

    return {
      baseHeaderLabel: `Base model ${metricHeaderLabel}`,
      postUnlearningHeaderLabel: `Post-unlearning ${metricHeaderLabel}`,
    }
  }

  const buildComparisonRows = (
    baseSummaryRows: SummaryRow[],
    unlearnedSummaryRows: SummaryRow[],
    framework: EvalFramework,
  ) => {
    const baseRowsByKey = new Map(
      baseSummaryRows.map((baseSummaryRow) => [
        buildComparisonKey(baseSummaryRow, framework),
        baseSummaryRow,
      ]),
    )

    const rows: ComparisonRow[] = []
    for (const unlearnedSummaryRow of unlearnedSummaryRows) {
      const rowKey = buildComparisonKey(unlearnedSummaryRow, framework)
      const baseSummaryRow = baseRowsByKey.get(rowKey)
      const baseValue = baseSummaryRow
        ? getMetricValueFromSummaryRow(baseSummaryRow, framework)
        : null
      const unlearnedValue = getMetricValueFromSummaryRow(unlearnedSummaryRow, framework)
      const baseNumberValue = parseNumber(baseValue)
      const unlearnedNumberValue = parseNumber(unlearnedValue)

      rows.push({
        metricName: getSummaryColumnValue(unlearnedSummaryRow, 'metric'),
        label: getComparisonLabel(unlearnedSummaryRow, framework),
        baseValue,
        unlearnedValue,
        reductionPercent:
          framework === EvalFramework.INSPECT_EVALS
            ? baseNumberValue !== null && unlearnedNumberValue !== null
              ? Math.abs(unlearnedNumberValue - baseNumberValue)
              : null
            : baseNumberValue !== null && unlearnedNumberValue !== null && baseNumberValue !== 0
              ? ((baseNumberValue - unlearnedNumberValue) / baseNumberValue) * 100
              : null,
      })
    }

    comparisonRows = rows
  }

  const findCompletedBaseEvalResultUrl = async (
    currentRunInfo: RunInfo,
  ): Promise<string | null> => {
    const sourceModelId = currentRunInfo.sourceRun?.modelId
    if (!sourceModelId) {
      return null
    }

    const listResponse = await handleFetch('/llm-behavior-eval/run/list?archived=false', {
      method: 'GET',
      allowErrorResponse: true,
    })
    if (!listResponse.ok) {
      return null
    }

    const listPayload = camelcaseKeys(await listResponse.json(), { deep: true }) as ListEvalRun[]
    const matchingBaseRunIdentifiers = listPayload
      .filter((listedRun) => {
        return (
          listedRun.modelId === sourceModelId &&
          !listedRun.sourceRunId &&
          listedRun.framework === currentRunInfo.framework &&
          (currentRunInfo.framework !== EvalFramework.INSPECT_EVALS ||
            (normalizeValue(listedRun.sampleLimit) === normalizeValue(currentRunInfo.sampleLimit) &&
              normalizeValue(listedRun.sampleShuffleSeed) ===
                normalizeValue(currentRunInfo.sampleShuffleSeed) &&
              normalizeValue(listedRun.attemptTimeout) ===
                normalizeValue(currentRunInfo.attemptTimeout) &&
              normalizeValue(listedRun.maxRetries) === normalizeValue(currentRunInfo.maxRetries) &&
              normalizeValue(listedRun.maxModelLen) ===
                normalizeValue(currentRunInfo.maxModelLen) &&
              JSON.stringify(listedRun.modelConfiguration ?? null) ===
                JSON.stringify(currentRunInfo.modelConfiguration ?? null) &&
              JSON.stringify([...(listedRun.taskIds ?? [])].sort()) ===
                JSON.stringify([...(currentRunInfo.taskIds ?? [])].sort()))) &&
          normalizeValue(listedRun.presetType) === normalizeValue(currentRunInfo.presetType) &&
          normalizeValue(listedRun.biasType) === normalizeValue(currentRunInfo.biasType) &&
          judgeModelsMatch(listedRun.judgeModel, currentRunInfo.judgeModel)
        )
      })
      .map((listedRun) => listedRun.runId)

    for (const baseRunIdentifier of matchingBaseRunIdentifiers) {
      const baseRunInfoResponse = await handleFetch(
        `/llm-behavior-eval/run/info/${baseRunIdentifier}`,
        {
          method: 'GET',
          allowErrorResponse: true,
        },
      )
      if (!baseRunInfoResponse.ok) {
        continue
      }
      const baseRunInfo = camelcaseKeys(await baseRunInfoResponse.json(), { deep: true }) as RunInfo
      if (baseRunInfo.status === LlmEvalStatus.SUCCESS && baseRunInfo.responsesZipUrl) {
        return baseRunInfo.responsesZipUrl
      }
    }

    return null
  }

  const loadEvalSummary = async (downloadUrl: string) => {
    if (hasLoadedSummary || isSummaryLoading) {
      return
    }
    isSummaryLoading = true
    summaryErrorMessage = null
    try {
      const { headers, rows } = await getSummaryFromDownloadUrl(downloadUrl)
      summaryHeaders =
        runInfo?.framework === EvalFramework.INSPECT_EVALS
          ? ['Benchmark', 'Metric', 'Score', 'Runtime (sec)'].filter((header) =>
              headers.includes(header),
            )
          : headers
      summaryRows = rows
      hasLoadedSummary = true
    } catch (error) {
      console.error('Failed to load eval summary', error)
      summaryErrorMessage =
        error instanceof Error ? error.message : 'Failed to load evaluation summary'
    } finally {
      isSummaryLoading = false
    }
  }

  const loadComparisonSummary = async (currentRunInfo: RunInfo, currentRunResultUrl: string) => {
    if (hasLoadedComparison || isComparisonLoading || !currentRunInfo.sourceRunId) {
      return
    }

    isComparisonLoading = true
    comparisonErrorMessage = null
    try {
      const baseRunResultUrl = await findCompletedBaseEvalResultUrl(currentRunInfo)
      if (!baseRunResultUrl) {
        hasLoadedComparison = true
        comparisonRows = []
        return
      }

      const baseSummaryPromise = getSummaryFromDownloadUrl(baseRunResultUrl)
      const unlearnedSummaryRowsPromise = hasLoadedSummary
        ? Promise.resolve(summaryRows)
        : getSummaryFromDownloadUrl(currentRunResultUrl).then(({ rows }) => rows)

      const [baseSummary, unlearnedSummaryRows] = await Promise.all([
        baseSummaryPromise,
        unlearnedSummaryRowsPromise,
      ])

      buildComparisonRows(baseSummary.rows, unlearnedSummaryRows, currentRunInfo.framework)
      if (currentRunInfo.framework === EvalFramework.INSPECT_EVALS) {
        comparisonMetric = {
          baseHeaderLabel: 'Base model score',
          postUnlearningHeaderLabel: 'Post-unlearning score',
        }
        comparisonChangeHeader = 'Delta'
      } else {
        comparisonMetric = getComparisonMetric(unlearnedSummaryRows)
        comparisonChangeHeader = 'Reduction %'
      }
      hasLoadedComparison = true
    } catch (error) {
      console.error('Failed to load comparison summary', error)
      comparisonErrorMessage =
        error instanceof Error ? error.message : 'Failed to load base-model comparison summary'
    } finally {
      isComparisonLoading = false
    }
  }

  const loadModelSummaries = async (currentRunInfo: RunInfo, currentRunResultUrl: string) => {
    if (hasAttemptedModelSummaryLoad || isSummaryLoading || isComparisonLoading) {
      return
    }

    const summaryLoadPromise = loadEvalSummary(currentRunResultUrl)
    const comparisonLoadPromise = currentRunInfo.sourceRunId
      ? loadComparisonSummary(currentRunInfo, currentRunResultUrl)
      : Promise.resolve()

    await Promise.allSettled([summaryLoadPromise, comparisonLoadPromise])
    hasAttemptedModelSummaryLoad = true
  }

  onMount(async () => {
    const runId = $modalStore[0]?.value?.runId as string
    try {
      const infoResponse = await handleFetch(`/llm-behavior-eval/run/info/${runId}`, {
        method: 'GET',
        allowErrorResponse: true,
      })
      if (!infoResponse.ok) {
        loadErrorMessage = `Failed to load evaluation run: ${await getResponseErrorMessage(
          infoResponse,
        )}`
        return
      }

      runInfo = camelcaseKeys(await infoResponse.json(), { deep: true }) as RunInfo
      status = runInfo.status
      if (runInfo.responsesZipUrl) {
        resultUrl = runInfo.responsesZipUrl
      }

      if (runInfo.status === LlmEvalStatus.SUCCESS && resultUrl) {
        await loadModelSummaries(runInfo, resultUrl)
        return
      }

      const stream = await handleSse(`/llm-behavior-eval/run/${runId}`, { method: 'GET' })
      for await (const event of stream) {
        if (!event) {
          continue
        }
        const json = await event.json()
        const eventState = json?.data?.state
        if (eventState === 'ERROR') {
          loadErrorMessage = json?.data?.result || 'Evaluation run failed'
          break
        }
        if (!Object.values(LlmEvalStatus).includes(eventState)) {
          loadErrorMessage = 'Evaluation run returned an unknown status'
          break
        }
        status = eventState
        resultUrl = json?.data?.result || resultUrl
        if (status === LlmEvalStatus.SUCCESS) {
          if (resultUrl) {
            await loadModelSummaries(runInfo, resultUrl)
          }
          break
        }
      }
    } catch (error) {
      loadErrorMessage = error instanceof Error ? error.message : 'Failed to load evaluation run'
    }
  })
</script>

{#if $modalStore[0]}
  <div
    class="card w-modal h-[80vh] min-w-[50vw] space-y-4 overflow-y-scroll px-4 shadow-xl"
    role="dialog"
  >
    <h3 class="h3 sticky top-0 justify-between bg-surface-50 p-4 dark:bg-surface-800">
      <div class="max-w-[90%]">Evaluation Run: {runInfo?.name}</div>
      <CloseModalButton onClose={parent.onClose} />
    </h3>
    <article class="space-y-4 p-4">
      {#if status === LlmEvalStatus.SUCCESS && resultUrl}
        <a class="variant-filled-primary btn" href={resultUrl} target="_blank" rel="noreferrer"
          >Download responses</a
        >
      {:else if loadErrorMessage}
        <p class="text-sm text-error-600" role="alert">{loadErrorMessage}</p>
      {:else}
        <div>
          {#if status}
            {getStatusLabel(status)}
          {:else}
            Loading...
          {/if}
        </div>
      {/if}

      {#if runInfo?.presetType}
        <dl class="grid grid-cols-[auto_1fr] gap-x-3 text-sm">
          <dt class="font-semibold">Benchmark</dt>
          <dd>{presetLabels[runInfo.presetType]}</dd>
        </dl>
      {/if}

      {#if status === LlmEvalStatus.SUCCESS}
        {#if !hasAttemptedModelSummaryLoad}
          <p class="text-sm text-surface-300">Loading evaluation details...</p>
        {:else}
          {#if runInfo?.sourceRunId}
            <div class="space-y-2">
              <h4 class="text-lg font-semibold">Base vs unlearned comparison</h4>
              {#if comparisonErrorMessage}
                <p class="text-sm text-error-600">{comparisonErrorMessage}</p>
              {:else if comparisonRows.length}
                <div class="table-container">
                  <table class="table w-full caption-bottom">
                    <thead>
                      <tr>
                        <th class="p-2">Metric</th>
                        <th class="p-2">{comparisonMetric.baseHeaderLabel}</th>
                        <th class="p-2">{comparisonMetric.postUnlearningHeaderLabel}</th>
                        <th class="p-2">{comparisonChangeHeader}</th>
                      </tr>
                    </thead>
                    <tbody class="[&>tr]:hover:preset-tonal-primary">
                      {#each comparisonRows as comparisonRow, comparisonRowIndex (comparisonRowIndex)}
                        <tr>
                          <td class="p-2">{comparisonRow.label}</td>
                          <td class="p-2"
                            >{formatDisplayMetricValue(
                              comparisonRow.baseValue,
                              runInfo?.framework,
                              comparisonRow.metricName,
                            )}</td
                          >
                          <td class="p-2"
                            >{formatDisplayMetricValue(
                              comparisonRow.unlearnedValue,
                              runInfo?.framework,
                              comparisonRow.metricName,
                            )}</td
                          >
                          <td class="p-2"
                            >{formatDisplayMetricValue(
                              comparisonRow.reductionPercent,
                              runInfo?.framework,
                              comparisonRow.metricName,
                              true,
                            )}</td
                          >
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {:else}
                <p class="text-sm text-surface-300">
                  No completed base-model evaluation found for comparison yet.
                </p>
              {/if}
            </div>
          {/if}

          {#if summaryRows.length || comparisonRows.length === 0}
            <div class="space-y-2">
              <h4 class="text-lg font-semibold">Evaluation summary</h4>
              {#if summaryErrorMessage}
                <p class="text-sm text-error-600">{summaryErrorMessage}</p>
              {:else if summaryRows.length}
                <div class="table-container">
                  <table class="table w-full caption-bottom">
                    <thead>
                      <tr>
                        {#each summaryHeaders as headerName (headerName)}
                          <th class="p-2">{getSummaryHeaderLabel(headerName)}</th>
                        {/each}
                      </tr>
                    </thead>
                    <tbody class="[&>tr]:hover:preset-tonal-primary">
                      {#each summaryRows as summaryRow, summaryRowIndex (summaryRowIndex)}
                        <tr>
                          {#each summaryHeaders as headerName (headerName)}
                            <td class="p-2"
                              >{formatSummaryValue(
                                headerName,
                                summaryRow[headerName],
                                runInfo?.framework,
                                getSummaryColumnValue(summaryRow, 'metric') ?? undefined,
                              )}</td
                            >
                          {/each}
                        </tr>
                      {/each}
                    </tbody>
                  </table>
                </div>
              {:else}
                <p class="text-sm text-surface-300">No summary data available.</p>
              {/if}
            </div>
          {/if}
        {/if}
      {/if}
    </article>
    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <button class="btn {parent.buttonNeutral}" onclick={() => modalStore.close()}>
        {parent.buttonTextCancel}
      </button>
    </footer>
  </div>
{/if}
